import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Eye, Minus } from 'lucide-react';
import type { AnchorRect } from './anchors';
import type { TourAction, TourOverlay } from './tour.types';
import { useIsNarrowScreen } from './useIsNarrowScreen';

interface TourCalloutProps {
  overlay: TourOverlay;
  /** Null for modals, or when a tooltip's anchor could not be found. */
  rect: AnchorRect | null;
  stepNumber: number;
  stepCount: number;
  onAction: (action: TourAction) => void;
  /** A link inside the overlay's own body was clicked. */
  onBodyLinkClick?: (label: string) => void;
}

const CARD =
  'bg-rs-off-black/95 backdrop-blur-md text-rs-off-white border border-rs-jet-light/60 ' +
  'rounded-xl shadow-2xl shadow-black/50 overflow-hidden';
const MAX_CARD_WIDTH = 380;
const SCREEN_EDGE_PADDING = 12;
const GAP_FROM_ANCHOR = 34;
const ARROW_SIZE = 16;
/** Less room than this below the anchor and the card is centered instead. A full-height
 *  anchor like the left nav has nothing below it to sit in. */
const MIN_SPACE_BELOW_ANCHOR = 240;

type ArrowSide = 'top' | 'bottom' | 'left' | 'right';

interface CardPosition {
  style: React.CSSProperties;
  arrowSide: ArrowSide;
  arrowOffset: number | string;
  cardWidth: number;
}

/** Keeps the card on screen near an edge. The tip tracks the anchor's center, not the
 *  card's. Null when there is nowhere sensible to anchor it, so it gets centered. */
function calculateCardPosition(
  rect: AnchorRect,
  placement: string,
  cardHeight: number,
  /** Set once a step is already anchored, so scrolling can't send it to the middle. */
  stayAnchored = false,
): CardPosition | null {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  // Narrow screens get a narrower card instead of one running off the edge.
  const cardWidth = Math.min(MAX_CARD_WIDTH, screenWidth - SCREEN_EDGE_PADDING * 2);
  const anchorCenterX = rect.left + rect.width / 2;
  const anchorCenterY = rect.top + rect.height / 2;

  // Nothing fits beside the anchor, so sit below it instead.
  const spaceLeftOfAnchor = rect.left - GAP_FROM_ANCHOR - cardWidth;
  const spaceRightOfAnchor =
    screenWidth - (rect.left + rect.width + GAP_FROM_ANCHOR + cardWidth);
  let resolvedPlacement = placement;
  if (
    (placement === 'left' && spaceLeftOfAnchor < SCREEN_EDGE_PADDING) ||
    (placement === 'right' && spaceRightOfAnchor < SCREEN_EDGE_PADDING)
  ) {
    resolvedPlacement = 'bottom';
  }

  // Fits the whole card, not just its top edge. A full-height anchor like the left nav
  // used to push everything below it off the bottom of the screen.
  const fitOnScreen = (top: number) =>
    Math.max(
      SCREEN_EDGE_PADDING,
      Math.min(top, screenHeight - cardHeight - SCREEN_EDGE_PADDING),
    );

  switch (resolvedPlacement) {
    case 'top': {
      const left = clampToScreen(anchorCenterX - cardWidth / 2, screenWidth, cardWidth);
      return {
        style: { top: fitOnScreen(rect.top - GAP_FROM_ANCHOR - cardHeight), left },
        arrowSide: 'bottom',
        arrowOffset: clampArrowToCard(anchorCenterX - left, cardWidth),
        cardWidth,
      };
    }
    case 'left': {
      const left = Math.max(SCREEN_EDGE_PADDING, rect.left - GAP_FROM_ANCHOR - cardWidth);
      const top = fitOnScreen(anchorCenterY - cardHeight / 2);
      return {
        style: { top, left },
        arrowSide: 'right',
        arrowOffset: clampArrowToCard(anchorCenterY - top, cardHeight),
        cardWidth,
      };
    }
    case 'right': {
      const left = Math.min(
        rect.left + rect.width + GAP_FROM_ANCHOR,
        screenWidth - cardWidth - SCREEN_EDGE_PADDING,
      );
      const top = fitOnScreen(anchorCenterY - cardHeight / 2);
      return {
        style: { top, left },
        arrowSide: 'left',
        arrowOffset: clampArrowToCard(anchorCenterY - top, cardHeight),
        cardWidth,
      };
    }
    case 'bottom':
    default: {
      const left = clampToScreen(anchorCenterX - cardWidth / 2, screenWidth, cardWidth);
      // Always sit directly below the anchor. Running this through fitOnScreen used to
      // pull a tall card back up over the element it points at, which is what made the
      // card load above the button on mobile.
      const top = rect.top + rect.height + GAP_FROM_ANCHOR;
      const spaceBelowAnchor = screenHeight - top - SCREEN_EDGE_PADDING;
      if (!stayAnchored && spaceBelowAnchor < MIN_SPACE_BELOW_ANCHOR) return null;
      // Even an already-anchored card gets centered rather than parked below the fold.
      if (spaceBelowAnchor <= 0) return null;
      return {
        style: { top, left },
        arrowSide: 'top',
        arrowOffset: clampArrowToCard(anchorCenterX - left, cardWidth),
        cardWidth,
      };
    }
  }
}

function clampToScreen(left: number, screenWidth: number, cardWidth: number): number {
  return Math.max(
    SCREEN_EDGE_PADDING,
    Math.min(left, screenWidth - cardWidth - SCREEN_EDGE_PADDING),
  );
}

/** Keeps the tip clear of the card's rounded corners. */
function clampArrowToCard(offset: number, cardWidth: number): number {
  return Math.max(20, Math.min(offset, cardWidth - 20));
}

const ARROW_EDGE: Record<ArrowSide, string> = {
  top: 'border-t border-l',
  bottom: 'border-b border-r',
  left: 'border-l border-b',
  right: 'border-t border-r',
};

const Arrow: React.FC<{ side: ArrowSide; offset: number | string }> = ({ side, offset }) => {
  const half = -ARROW_SIZE / 2;
  const placement =
    side === 'top' || side === 'bottom'
      ? { [side]: half, left: offset, marginLeft: half }
      : { [side]: half, top: offset, marginTop: half };

  return (
    <div
      className={`absolute rotate-45 bg-rs-green border-rs-jet-light/60 ${ARROW_EDGE[side]}`}
      style={{ width: ARROW_SIZE, height: ARROW_SIZE, ...placement } as React.CSSProperties}
    />
  );
};

/** Ring over the anchored element, so the tour's target is obvious. */
const Highlight: React.FC<{ rect: AnchorRect }> = ({ rect }) => (
  <div
    className="tour-pulse fixed z-[9999] pointer-events-none rounded-lg ring-2 ring-rs-green"
    style={{
      top: rect.top - 6,
      left: rect.left - 6,
      width: rect.width + 12,
      height: rect.height + 12,
    }}
  />
);

const ProgressBar: React.FC<{ step: number; total: number }> = ({ step, total }) => (
  <div className="h-1.5 w-full shrink-0 bg-rs-jet-light/40">
    <div
      className="h-full bg-rs-green transition-all duration-300"
      style={{ width: `${(step / total) * 100}%` }}
    />
  </div>
);

// Green is reserved for the CTA, so Next/primary stays a muted, background-free button.
const PRIMARY = 'bg-transparent text-rs-off-white hover:bg-rs-jet-light/40';
const CTA = 'bg-rs-green text-black hover:opacity-85';
const SECONDARY = 'bg-transparent text-rs-off-white/60 hover:bg-rs-jet-light/40';

function buttonClass(type: TourAction['type']): string {
  if (type === 'cta') return CTA;
  if (type === 'restart' || type === 'back') return SECONDARY;
  return PRIMARY;
}

const Actions: React.FC<{ actions: TourAction[]; onAction: (a: TourAction) => void }> = ({
  actions,
  onAction,
}) => (
  <div className="flex items-center justify-center gap-3 pt-3 pb-1">
    {actions.map((action) => {
      // Opening-modal CTA: same green as the title, blinking to draw the eye.
      const isStartTour = action.label.toLowerCase() === 'start the tour';
      return (
        <button
          key={action.label}
          onClick={() => onAction(action)}
          className={`px-5 py-2.5 rounded-md font-semibold transition-colors ${
            isStartTour ? `${CTA} tour-blink tour-start-pulse` : buttonClass(action.type)
          }`}
        >
          {action.label}
        </button>
      );
    })}
  </div>
);

const TourCallout: React.FC<TourCalloutProps> = ({
  overlay,
  rect,
  stepNumber,
  stepCount,
  onAction,
  onBodyLinkClick,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const isNarrowScreen = useIsNarrowScreen();
  const cardRef = useRef<HTMLDivElement>(null);
  const stepLayout = useRef<{ id: string; anchored: boolean } | null>(null);
  const [cardHeight, setCardHeight] = useState(0);

  // Measured before paint, so the card is positioned against its real height.
  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [overlay.id, overlay.body, rect, collapsed]);

  const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const link = (e.target as HTMLElement).closest('a');
    if (link) onBodyLinkClick?.(link.textContent?.trim() ?? '');
  };

  // A new step always brings the card back.
  useEffect(() => setCollapsed(false), [overlay.id]);

  const defined = overlay.actions?.length
    ? overlay.actions
    : [{ label: 'Next', type: 'next' as const }];
  // Every step except the first can navigate back.
  const actions = stepNumber > 1 ? [{ label: 'Back', type: 'back' as const }, ...defined] : defined;

  const content = (
    <>
      <ProgressBar step={stepNumber} total={stepCount} />
      {/* tour-blink rather than tour-pulse: the card's overflow-hidden clips a
          box-shadow ring, but a fade survives inside it. */}
      <button
        onClick={() => setCollapsed(true)}
        className={`absolute top-3 right-3 flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-rs-off-white/50 hover:text-rs-off-white hover:bg-rs-jet-light/50 transition-colors ${
          overlay.highlightsMinimizeButton ? 'tour-blink bg-rs-jet-light/50' : ''
        }`}
      >
        <Minus size={14} className="text-rs-green border border-rs-green rounded-full p-0.5" />
      </button>
      <div className="px-7 py-6 min-h-0 overflow-y-auto">
        {overlay.title && (
          <h3 className="text-lg font-bold mb-3 text-rs-green">{overlay.title}</h3>
        )}
        <div
          className="tour-body text-[15px] leading-relaxed text-rs-off-white/80 space-y-3"
          dangerouslySetInnerHTML={{ __html: overlay.body }}
          onClick={handleBodyClick}
        />
        <Actions actions={actions} onAction={onAction} />
      </div>
    </>
  );

  if (collapsed) {
    return (
      <>
        {rect && <Highlight rect={rect} />}
        <div
          data-tour-ui
          className={`fixed top-1/2 -translate-y-1/2 z-[10000] ${
            isNarrowScreen ? 'inset-x-0 flex justify-center' : 'right-80'
          }`}
        >
          <button
            onClick={() => setCollapsed(false)}
            className="tour-pulse flex items-center gap-2 rounded-full px-4 py-2.5 bg-rs-off-black/95 backdrop-blur-md border border-rs-jet-light/60 text-rs-off-white shadow-2xl shadow-black/50 hover:border-rs-green transition-colors"
          >
            <Eye size={16} className="text-rs-green" />
            <span className="text-sm font-semibold">Resume tour</span>
            <span className="text-xs text-rs-off-white/50">
              {stepNumber}/{stepCount}
            </span>
          </button>
        </div>
      </>
    );
  }

  if (stepLayout.current && stepLayout.current.id !== overlay.id) stepLayout.current = null;

  const settled = stepLayout.current;

  const position = rect
    ? calculateCardPosition(
        rect,
        overlay.placement ?? 'bottom',
        cardHeight,
        settled?.anchored === true,
      )
    : null;

  // Only ever remember that a step anchored. Recording "centred" too would freeze a
  // decision made from an early measurement, before the page had finished settling.
  if (rect && position && !settled) {
    stepLayout.current = { id: overlay.id, anchored: true };
  }

  // Scrolled far enough that the element is gone, so the card has nothing to point at.
  const anchorOutOfView =
    !!rect && (rect.top + rect.height < 0 || rect.top > window.innerHeight);

  // A modal, a tooltip whose anchor went missing, or one with nowhere to sit next to
  // its anchor: center it, still ringing the anchor when there is one.
  const showCentered = overlay.type === 'modal' || !rect || !position || anchorOutOfView;

  if (showCentered) {
    return (
      <>
        {rect && <Highlight rect={rect} />}
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 pointer-events-none">
          <div
            data-tour-ui
            className={`${CARD} tour-modal-hover relative pointer-events-auto flex flex-col max-h-full w-[min(680px,92vw)]`}
          >
            {content}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Highlight rect={rect} />
      <div
        ref={cardRef}
        data-tour-ui
        className="fixed z-[10000] pointer-events-auto"
        style={{ ...position.style, width: position.cardWidth }}
      >
        <Arrow side={position.arrowSide} offset={position.arrowOffset} />
        <div className={`${CARD} tour-modal-hover relative`}>{content}</div>
      </div>
    </>
  );
};

export default TourCallout;
